import type { CampoBinario, CampoOpcion, CampoPuntaje, Respuesta } from '@pasos/schemas';
import { useId } from 'react';
import { Opcion } from '../../components/domain/Opcion.js';

type CampoDeLista = CampoOpcion | CampoBinario | CampoPuntaje;

type OpcionesProps = {
  campo: CampoDeLista;
  valor: Respuesta | undefined;
  onElegir: (valor: Respuesta) => void;
  /** Id del encabezado de la pregunta: es el nombre accesible del grupo. */
  etiquetadoPor: string;
};

type Elegible = { clave: string; etiqueta: string; valor: Respuesta };

function elegibles(campo: CampoDeLista): Elegible[] {
  if (campo.tipo === 'binario') {
    return [
      { clave: 'si', etiqueta: campo.etiquetaSi, valor: true },
      { clave: 'no', etiqueta: campo.etiquetaNo, valor: false },
    ];
  }

  // Rango corto declarado como lista: son los cinco niveles de cada dimensión
  // del EQ-5D-5L. La etiqueta visible es el nivel; la redacción oficial entra
  // con la licencia de EuroQol y viaja en el instrumento, no acá.
  if (campo.tipo === 'puntaje') {
    return Array.from({ length: campo.maximo - campo.minimo + 1 }, (_, indice) => {
      const nivel = campo.minimo + indice;
      return { clave: String(nivel), etiqueta: String(nivel), valor: nivel };
    });
  }

  return campo.opciones.map((opcion) => ({
    clave: opcion.codigo,
    etiqueta: opcion.etiqueta,
    valor: opcion.codigo,
  }));
}

/**
 * Lista de opciones · una por fila, a lo ancho.
 *
 * **Radios nativos dentro de un `fieldset`**, no botones con `role="radio"`. La
 * diferencia importa de verdad: el navegador se encarga solo del recorrido con
 * flechas, del anuncio "opción 2 de 4" y de la agrupación, y ninguna de esas tres
 * cosas hay que reimplementarlas ni mantenerlas. El input está visualmente
 * oculto pero **no escondido del árbol de accesibilidad** (`sr-only`, no
 * `display:none`): sigue recibiendo foco y el `label` que lo envuelve es el
 * blanco de 56px.
 *
 * El grupo toma su nombre del encabezado visible con `aria-labelledby`, en vez
 * de repetirlo en una `legend` oculta: con las dos cosas, un lector de pantalla
 * lee la pregunta dos veces seguidas.
 *
 * La opción elegida se marca con **anillo, fondo y sombra**, nunca con un borde
 * izquierdo de color — el cliché que el sistema visual del proyecto rechaza.
 *
 * **También resuelve los puntajes de rango corto declarados como `lista`.** Las
 * cinco dimensiones del EQ-5D-5L son eso, y la escala de caras es del microcheck
 * y sólo del microcheck (ADR-016, enmienda del 25/08): una escala validada no se
 * responde con caritas.
 *
 * E3.8.2, contra la referencia P-05c: cada opción es la `Opcion` del sistema
 * visual —72 px, radio grande que se llena con tilde, halo al elegirla—, con el
 * mismo radio nativo adentro.
 */
export function Opciones({ campo, valor, onElegir, etiquetadoPor }: OpcionesProps) {
  const grupo = useId();

  return (
    <fieldset aria-labelledby={etiquetadoPor} className="m-0 flex flex-col gap-3 border-0 p-0">
      {elegibles(campo).map((opcion) => (
        <Opcion
          elegida={valor === opcion.valor}
          key={opcion.clave}
          nombre={grupo}
          onElegir={() => onElegir(opcion.valor)}
          titulo={opcion.etiqueta}
          valor={opcion.clave}
        />
      ))}
    </fieldset>
  );
}
